import { useTrustGraphView } from '@/hooks/use-trust-graph';
import { cn } from '@/lib/utils';
import type { TrustGraphViewQuery } from '@uncain/shared';
import { startTransition, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { TrustGraphCanvas } from './trust-graph-canvas';
import { filterTrustGraphView } from './trust-graph-data';
import { TrustGraphInspector } from './trust-graph-inspector';
import { TrustGraphLegend } from './trust-graph-legend';
import { TrustGraphToolbar } from './trust-graph-toolbar';

interface TrustGraphPanelProps {
	readonly query: TrustGraphViewQuery;
	readonly compact?: boolean;
	readonly className?: string;
	readonly emptyLabel?: string;
}

export function TrustGraphPanel({
	query,
	compact = false,
	className,
	emptyLabel = 'No graph data is available for this scope yet.',
}: TrustGraphPanelProps) {
	const { data, isLoading, error } = useTrustGraphView(query);
	const [searchTerm, setSearchTerm] = useState('');
	const [showHiddenNodes, setShowHiddenNodes] = useState(false);
	const [riskOnly, setRiskOnly] = useState(false);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const deferredSearch = useDeferredValue(searchTerm);

	useEffect(() => {
		if (!data) {
			return;
		}
		setSelectedNodeId(
			data.defaultFilters.focusNodeIds[0] ??
				data.defaultFilters.highlightedNodeIds[0] ??
				data.nodes[0]?.id ??
				null,
		);
	}, [data]);

	const filtered = useMemo(() => {
		if (!data) {
			return null;
		}
		return filterTrustGraphView(data, {
			searchTerm: deferredSearch,
			showHiddenNodes,
			riskOnly,
			pinnedNodeId: selectedNodeId,
		});
	}, [data, deferredSearch, riskOnly, selectedNodeId, showHiddenNodes]);

	if (isLoading) {
		return (
			<div
				className={cn(
					'rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground',
					className,
				)}
			>
				Loading trust graph...
			</div>
		);
	}

	if (error || !data) {
		return (
			<div
				className={cn(
					'rounded-2xl border border-destructive/20 bg-card p-6 text-sm text-destructive',
					className,
				)}
			>
				Failed to load trust graph data.
			</div>
		);
	}

	if (data.nodes.length === 0 || !filtered) {
		return (
			<div
				className={cn(
					'rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground',
					className,
				)}
			>
				{emptyLabel}
			</div>
		);
	}

	return (
		<div
			className={cn(
				'space-y-4 rounded-[1.75rem] border border-border/70 bg-card p-5 shadow-sm',
				className,
			)}
		>
			<div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
				<div>
					<div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
						Trust Graph
					</div>
					<h2 className="mt-1 text-xl font-semibold text-foreground">{data.title}</h2>
					{data.subtitle && <p className="mt-1 text-sm text-muted-foreground">{data.subtitle}</p>}
				</div>
				<div className="text-xs text-muted-foreground">
					{filtered.nodes.length} of {data.stats.totalNodes} nodes visible
				</div>
			</div>

			<TrustGraphToolbar
				metrics={data.stats.metrics}
				searchTerm={searchTerm}
				onSearchChange={(value) => startTransition(() => setSearchTerm(value))}
				showHiddenNodes={showHiddenNodes}
				onToggleHiddenNodes={() => setShowHiddenNodes((value) => !value)}
				riskOnly={riskOnly}
				onToggleRiskOnly={() => setRiskOnly((value) => !value)}
				compact={compact}
			/>

			{filtered.nodes.length === 0 ? (
				<div className="rounded-2xl border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
					No nodes match the current graph filters.
				</div>
			) : (
				<>
					<div
						className={cn(
							'grid gap-4',
							compact ? 'grid-cols-1' : 'xl:grid-cols-[minmax(0,1.7fr)_22rem]',
						)}
					>
						<div className="space-y-0">
							<TrustGraphCanvas
								elements={filtered.elements}
								selectedNodeId={selectedNodeId}
								onSelectNode={setSelectedNodeId}
								scope={data.scope}
								compact={compact}
							/>
							<TrustGraphLegend legend={data.legend} compact />
						</div>
						{!compact && (
							<div className="space-y-4">
								<TrustGraphInspector node={filtered.selectedNode} />
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
